import {
  CustomerPortalVersion,
  doesCustomerPortalSupportBulkReturns,
  NonZeroValueExchangeStrategy,
  Team,
} from "@redotech/redo-model/team";
import { Card } from "@redotech/redo-web/card";
import * as gridCss from "@redotech/redo-web/grid.module.css";
import { FormSelectDropdown } from "@redotech/redo-web/select-dropdown";
import { FormSwitch } from "@redotech/redo-web/switch";
import { FormTextInput } from "@redotech/redo-web/text-input";
import { groupInput, input, InputProvider } from "@redotech/ui/form";
import classNames from "classnames";
import { memo } from "react";

export const returnsGeneralForm = groupInput({
  enabled: input<boolean>(),
  customerPortalVersion: input<CustomerPortalVersion>(),
  refundAllReturnedItems: input<boolean>(),
  skipExchangeRefunds: input<boolean>(),
  settlementEnabled: input<boolean>(),
  settlementRefund: input<string>(),
  allowMultiOrderReturns: input<boolean>(),
  multiOrderReturnLimit: input<string>(),
  disableRejectedItems: input<boolean>(),
  bulkReturnsEnabled: input<boolean>(),
  chargeMerchantForDamagedOrWrongProductReturnLabels: input<boolean>(),
  enableCustomerPortal: input<boolean>(),
  enableShopAppReturns: input<boolean>(),
  buyYourOwnLabel: input<boolean>(),
  nonZeroValueExchangeStrategy: input<NonZeroValueExchangeStrategy>(),
  agentReviewEnabled: input<boolean>(),
  agentChatEnabled: input<boolean>(),
});

export type ReturnsGeneralForm = InputProvider.Form<typeof returnsGeneralForm>;

export type ReturnsGeneralValue = InputProvider.Value<
  typeof returnsGeneralForm
>;

export function teamToReturnsGeneralFormValue(team: Team) {
  return {
    enabled: team.settings?.returns?.enabled ?? false,
    customerPortalVersion:
      team.settings?.customerPortalVersion || CustomerPortalVersion.V4_0,
    refundAllReturnedItems:
      team.settings?.returns?.refundAllReturnedItems ?? false,
    skipExchangeRefunds: team.settings?.returns?.skipExchangeRefunds ?? false,
    settlementEnabled: team.settings?.returns?.settlementEnabled ?? false,
    settlementRefund: (
      (team.settings?.returns?.settlementRefund || 0) * 100
    ).toString(),
    allowMultiOrderReturns:
      team.settings.returns?.allowMultiOrderReturns ?? false,
    multiOrderReturnLimit: team.settings.returns?.multiOrderReturnLimit
      ? String(team.settings.returns.multiOrderReturnLimit)
      : "0",
    disableRejectedItems: !!team.settings.returns?.disableRejectedItems,
    bulkReturnsEnabled: !!team.settings?.bulkReturnsEnabled,
    chargeMerchantForDamagedOrWrongProductReturnLabels:
      !team.settings.returns
        ?.dontChargeMerchantForDamagedOrWrongProductReturnLabels,
    enableCustomerPortal: team.settings.returns?.enableCustomerPortal ?? true,
    enableShopAppReturns: team.settings.returns?.enableShopAppReturns ?? false,
    buyYourOwnLabel: team.settings.returns?.buyYourOwnLabel ?? false,
    nonZeroValueExchangeStrategy:
      team.settings.exchanges?.nonZeroValueExchangeStrategy ??
      NonZeroValueExchangeStrategy.NONE,
    agentReviewEnabled: team.settings?.returns?.agentReviewEnabled ?? false,
    agentChatEnabled: team.settings?.returns?.agentChatEnabled ?? false,
  };
}

export const returnsGeneralDefault: ReturnsGeneralValue = {
  enabled: false,
  customerPortalVersion: CustomerPortalVersion.V4_0,
  refundAllReturnedItems: false,
  skipExchangeRefunds: false,
  settlementEnabled: false,
  settlementRefund: "0",
  allowMultiOrderReturns: false,
  multiOrderReturnLimit: "0",
  disableRejectedItems: false,
  bulkReturnsEnabled: false,
  chargeMerchantForDamagedOrWrongProductReturnLabels: true,
  enableCustomerPortal: true,
  enableShopAppReturns: false,
  buyYourOwnLabel: false,
  nonZeroValueExchangeStrategy: NonZeroValueExchangeStrategy.NONE,
  agentReviewEnabled: false,
  agentChatEnabled: false,
};

const customerPortalVersions: readonly CustomerPortalVersion[] = [
  CustomerPortalVersion.V3_5,
  CustomerPortalVersion.V4_0,
];

const nonZeroValueExchangeStrategies: readonly NonZeroValueExchangeStrategy[] =
  [
    NonZeroValueExchangeStrategy.NONE,
    NonZeroValueExchangeStrategy.APPLY_DISCOUNT,
    NonZeroValueExchangeStrategy.MARK_AS_PAID,
    NonZeroValueExchangeStrategy.DISCOUNTED_VALUE_MARKED_AS_PAID,
    NonZeroValueExchangeStrategy.CREATE_AND_COMPLETE_DRAFT_ORDER,
    NonZeroValueExchangeStrategy.UPSELL_VALUE_MARKED_AS_PAID,
    NonZeroValueExchangeStrategy.APPLY_EXCHANGE_DISCOUNT_WITH_TAX_MARK_AS_PAID,
  ];

export const ReturnsGeneralCard = memo(function ReturnsGeneralCard({
  input,
}: {
  input: ReturnsGeneralForm;
}) {
  const {
    enabled,
    refundAllReturnedItems,
    skipExchangeRefunds,
    settlementEnabled,
    settlementRefund,
    customerPortalVersion,
    allowMultiOrderReturns,
    multiOrderReturnLimit,
    disableRejectedItems,
    bulkReturnsEnabled,
    chargeMerchantForDamagedOrWrongProductReturnLabels,
    enableCustomerPortal,
    enableShopAppReturns,
    buyYourOwnLabel,
    nonZeroValueExchangeStrategy,
    agentReviewEnabled,
    agentChatEnabled,
  } = input.inputs;

  const minMaxError =
    parseInt(settlementRefund.value) < 0 ||
    parseInt(settlementRefund.value) > 100;
  return (
    <Card title="Returns">
      <section className={gridCss.grid}>
        <div className={classNames(gridCss.span12, gridCss.span6L)}>
          <FormSwitch input={enabled} label="Enabled">
            Enable Returns product
          </FormSwitch>
        </div>
        <div className={classNames(gridCss.span12, gridCss.span6L)}>
          <FormSelectDropdown
            input={customerPortalVersion}
            label="Return portal version"
            options={customerPortalVersions}
          >
            {(option) => option}
          </FormSelectDropdown>
        </div>
        <div className={classNames(gridCss.span12, gridCss.span6L)}>
          <FormSwitch
            input={allowMultiOrderReturns}
            label="Allow multi order returns (CommentSold only)"
          >
            A single return can be created from multiple orders
          </FormSwitch>
        </div>
        {allowMultiOrderReturns.value && (
          <div className={classNames(gridCss.span12, gridCss.span6L)}>
            <FormTextInput
              description="Include orders within this range before or after the reference order. Limited to 10 orders before and 10 orders after."
              input={multiOrderReturnLimit}
              label="Multi-order returns day range"
              min={0}
              type="number"
            />
          </div>
        )}
        <div className={classNames(gridCss.span12, gridCss.span6L)}>
          <FormSwitch
            input={refundAllReturnedItems}
            label="Always create refund"
          >
            Create a Shopify refund for all returned items, even store credit
            and exchanges. Note: If "Disable refund line items" in automation
            settings is on, it will override this setting.
          </FormSwitch>
        </div>
        {refundAllReturnedItems.value && (
          <div className={classNames(gridCss.span12, gridCss.span6L)}>
            <FormSwitch
              input={skipExchangeRefunds}
              label="Skip exchange refunds"
            >
              Skip creating $0 Shopify refunds for exchanges even when "Always
              create refund" is enabled. (Only if there is no refund and no
              store credit)
            </FormSwitch>
          </div>
        )}
        <div className={classNames(gridCss.span12, gridCss.span6L)}>
          <FormSwitch input={settlementEnabled} label="Allow Settlement" />
        </div>
        {settlementEnabled.value && (
          <div className={classNames(gridCss.span12, gridCss.span6L)}>
            <FormTextInput
              description={
                minMaxError && (
                  <span style={{ color: "red" }}>
                    Value must be between 0 and 100
                  </span>
                )
              }
              errorOverride={minMaxError}
              input={settlementRefund}
              label="Settlement Refund"
              max={100}
              min={0}
              suffix="%"
              type="number"
            />
            The percentage of the refund that the customer will receive if they
            choose to keep the item
          </div>
        )}
        <div className={classNames(gridCss.span12, gridCss.span6L)}>
          <FormSwitch
            input={disableRejectedItems}
            label="Pre-disable rejected items"
          >
            Show rejection message in tooltip instead of opening the return flow
            modal
          </FormSwitch>
        </div>
        {doesCustomerPortalSupportBulkReturns[customerPortalVersion.value] && (
          <div className={classNames(gridCss.span12, gridCss.span6L)}>
            <FormSwitch
              input={bulkReturnsEnabled}
              label="Enable bulk returns"
            />
          </div>
        )}
        <div className={classNames(gridCss.span6L)}>
          <FormSwitch
            input={chargeMerchantForDamagedOrWrongProductReturnLabels}
            label="Charge Merchants for damaged or wrong product return labels"
          />
        </div>
        <div className={classNames(gridCss.span6L)}>
          <FormSwitch
            input={enableCustomerPortal}
            label="Enable customer portal"
          >
            Allow customers to return through the customer portal
          </FormSwitch>
        </div>
        <div className={classNames(gridCss.span6L)}>
          <FormSwitch
            input={enableShopAppReturns}
            label="Enable Shop App returns"
          >
            Allow customers to return through the Shop App
          </FormSwitch>
        </div>
        <div className={classNames(gridCss.span6L)}>
          <FormSwitch input={buyYourOwnLabel} label="Enable Buy Your Own Label">
            Changes text during international green returns to prompt the
            shopper to buy their own label.
          </FormSwitch>
        </div>
        <div className={classNames(gridCss.span6L)}>
          <FormSelectDropdown
            input={nonZeroValueExchangeStrategy}
            label="Even exchange new order value"
            options={nonZeroValueExchangeStrategies}
          >
            {(option) => {
              switch (option) {
                case NonZeroValueExchangeStrategy.NONE:
                  return "Zero";
                case NonZeroValueExchangeStrategy.APPLY_DISCOUNT:
                  return "Full value discounted to zero";
                case NonZeroValueExchangeStrategy.MARK_AS_PAID:
                  return "Full value marked as paid";
                case NonZeroValueExchangeStrategy.DISCOUNTED_VALUE_MARKED_AS_PAID:
                  return "Discounted value marked as paid";
                case NonZeroValueExchangeStrategy.CREATE_AND_COMPLETE_DRAFT_ORDER:
                  return "Create and complete draft order";
                case NonZeroValueExchangeStrategy.UPSELL_VALUE_MARKED_AS_PAID:
                  return "Upsell value marked as paid";
                case NonZeroValueExchangeStrategy.APPLY_EXCHANGE_DISCOUNT_WITH_TAX_MARK_AS_PAID:
                  return "Full value with discount and tax applied, marked as paid";
                default:
                  return option;
              }
            }}
          </FormSelectDropdown>
        </div>
        <div className={classNames(gridCss.span6L)}>
          <FormSwitch
            input={agentReviewEnabled}
            label="Enable AI return review"
          >
            Show AI risk assessment and recommendations for returns
          </FormSwitch>
        </div>
        <div className={classNames(gridCss.span6L)}>
          <FormSwitch
            input={agentChatEnabled}
            label="Enable AI chat for returns"
          >
            Enable AI chat functionality for customer return inquiries
          </FormSwitch>
        </div>
      </section>
    </Card>
  );
});
